import React from 'react';

import { useTranslation } from 'react-i18next';

import {
    AuthStatus,
    Channel,
    EventMessage,
    EventType,
    JwtProcessStateManager,
    MenuOptions,
    sessionTimerManager,
    useAppBridge,
    useAuthStatus,
    useChannelContext,
    useEventBusEmit,
    useEventBusOn,
    useGlobalErrorStore,
    useMenuRouter,
    useQuery,
    useRouter,
    useUserContext,
    useWebviewBridge,
    useWebviewContext,
    useWebviewReactivated,
    WebviewVersion,
    type JwtEvent,
} from 'scbk-services';
import {
    Dialog,
    useLoading,
    useLogout,
    usePageMove,
    usePopupStore,
    useSecureKeypadStore,
    useSession,
} from 'scbk-shared-module';

//const SCHEMA_SCMOBILE = 'scmobile://';

const AppEventSubscriber: React.FC = () => {
    const { channel } = useChannelContext();
    const emit = useEventBusEmit();
    const { updateCommonContext: updateUserContext } = useUserContext();
    const { getStatus: getUserAuthStatus } = useAuthStatus();
    const { show: showLoadingBar, hide: hideLoadingBar } = useLoading();
    const { doNResetSessionTimer, doNStartSessionTimer, doNLogout, doNSetStatusBarIconColor } =
        useAppBridge();
    const { doNPageLoadCompleted, doNGetCurrentWebviewStatus } = useWebviewBridge();
    const { doNAutoLogout } = useAppBridge();
    const { doLogout } = useLogout();
    const { extendSession, isLogin } = useSession();
    const { goLogin } = usePageMove();
    const { goMenu, replaceMenu, goUrl } = useMenuRouter();
    const router = useRouter();
    const { getStatusBarColor, setStatusBarColor } = useWebviewContext();
    const { clear, subClear } = useSecureKeypadStore();
    const { clear: errorClear } = useGlobalErrorStore();
    const { reset: resetPopup } = usePopupStore();

    const webviewContext = useWebviewContext();
    const { query } = useQuery();
    const { doNCloseBizWebView } = useWebviewBridge();
    const { goMain } = usePageMove();

    const { t } = useTranslation();

    useEventBusOn({
        eventType: EventType.CertificatePinningFailed,
        listener: () => {
            emit(EventType.SessionInvalidated);
        },
    });

    //**********************************************
    //페이지 로드 완료 시 네이티브 브릿지 호출
    //**********************************************
    useEventBusOn({
        eventType: EventType.ScCoreReady,
        listener: async () => {
            if (!channel || channel !== Channel.MB) {
                return;
            }

            console.log('### EventType.ScCoreReady', channel);
            //페이지 로드 완료 브릿지 호출
            //await doNPageLoadCompleted();
        },
    });

    useEventBusOn({
        eventType: EventType.ScPageReady,
        listener: async () => {
            if (!channel || channel !== Channel.MB) {
                return;
            }

            console.log('### EventType.ScPageReady', channel);
            //페이지 로드 완료 브릿지 호출
            doNPageLoadCompleted();
        },
    });

    useEventBusOn({
        eventType: EventType.ForceRedirectLogin,
        listener: () => {
            const userStatus = getUserAuthStatus();
            if (userStatus === AuthStatus.UNAUTHENTICATED) {
                //navigate('/login?action=none');
            } else {
                //navigate('/login?action=logout');
            }
        },
    });

    useEventBusOn({
        eventType: EventType.UpdateSessionToken,
        listener: (token: unknown) => {
            const sessionToken = token as EventMessage.SessionToken;

            updateUserContext({
                accessToken: sessionToken.accessToken,
                refreshToken: sessionToken.refreshToken,
            });
        },
    });

    //*******************************************
    // JWT Forbiden 이벤트 처리
    //*******************************************
    const processManager: JwtProcessStateManager = JwtProcessStateManager.getInstance();
    useEventBusOn({
        eventType: EventType.JwtForbiden,
        listener: async (...args: unknown[]) => {
            const evt = args[0] as JwtEvent;
            console.log(`[JWT Event] event object : `, evt);

            const isOk = await Dialog.alert({
                title: `JWT 처리 오류[${evt.code}]`,
                content: `${evt.message}`,
            });

            console.log(`[JWT Event] isOk : ${isOk}`);
            if (isOk) {
                doLogout({
                    onLogout: () => {
                        //프리로그인 페이지로 이동
                        console.log(`[JWT Event] 프리로그인 페이지 이동`);
                        processManager.resetJwtProcessState(true);
                        goLogin({ isGoPreLogin: true });
                    },
                });
            }
        },
    });

    useEventBusOn({
        eventType: EventType.RouteHistoryEmpty,
        listener: async () => {
            console.log('# EventType.RouteHistoryEmpty doNCloseBizWebView');

            if (channel === Channel.MB) {
                const webviewType = query['webviewType'];
                if (webviewType === 'main') {
                    goMain({ type: 'HOME' });
                    return;
                }

                //현재 웹뷰 상태 inactive 로 업데이트
                webviewContext.inactiveWebview();
                doNCloseBizWebView({ type: webviewType as WebviewVersion });
            } else {
                //
                goMain({ type: 'HOME' });
            }
        },
    });

    //**********************************************
    //메뉴이동 이벤트
    //**********************************************
    useEventBusOn({
        eventType: EventType.MenuNavigationRequest,
        listener: async (...args: unknown[]) => {
            if (!channel) {
                return;
            }

            if (!args || args.length === 0) {
                return;
            }

            const [id, options] = args;
            const menuId = id as string;
            const opt = (options as MenuOptions | undefined) ?? {};

            if (menuId === 'MA3PRDALL001') {
                if (isLogin()) {
                    goMain({ type: 'PRODUCT', menuOptions: { ...opt } });
                    return;
                }
                opt.webviewType = 'v3';
            } else if (menuId === 'LGNADV000' || menuId === 'LGNMTH000') {
                goLogin({ isGoPreLogin: menuId === 'LGNADV000' });
            }

            const replace = opt?.replace ?? false;
            replace ? replaceMenu(menuId, opt) : goMenu(menuId, opt);
        },
    });

    //**********************************************
    //URL 요청처리 이벤트 (PMS 컨텐츠 클릭 이벤트 대응 loadNextPage, changeNextPage 등등)
    //**********************************************
    useEventBusOn({
        eventType: EventType.UrlNavigationRequest,
        listener: async (...args: unknown[]) => {
            console.log('### EventType.UrlNavigationRequest args=', args);
            if (!args || args.length === 0) {
                return;
            }

            const [url] = args as [string];
            if (!url) {
                return;
            }

            goUrl(url);
        },
    });

    //**********************************************
    //로그인 페이지 이동
    //**********************************************
    useEventBusOn({
        eventType: EventType.LoginNavigationRequest,
        listener: async (...args: unknown[]) => {
            const [options] = args;
            console.log('### options : ', options);
            goLogin({
                isGoPreLogin: true,
                menuOptions: { ...(options as MenuOptions) },
            });
        },
    });

    //**********************************************
    //프로그램 설치 페이지 이동
    //**********************************************
    useEventBusOn({
        eventType: EventType.ProgramInstallNavigationRequest,
        listener: async () => {
            const isOk = await Dialog.confirm({
                content: t(
                    'ProgramInstall.002',
                    '안전한 금융거래를 위해 필수 보안 프로그램 설치가 필요해요.<br/>설치 페이지로 이동할까요?',
                ),
            });

            if (isOk) {
                doLogout({
                    onLogout: async () => {
                        if (channel === Channel.MB) {
                            return;
                        }
                        goMenu('COMETC000');
                    },
                });
            }
        },
    });

    //**********************************************
    // 뒤로가기 처리
    //**********************************************
    useEventBusOn({
        eventType: EventType.RouteBackRequest,
        listener: async (...args) => {
            const [options] = args;
            console.log('### options : ', options);

            // Popup Store 클리어
            resetPopup();

            router.back({ ...(options as MenuOptions) });
        },
    });

    //**********************************************
    //로딩바 Show/Hide 이벤트 처리
    //**********************************************
    useEventBusOn({
        eventType: EventType.ShowLoadingBar,
        listener: () => {
            console.log('### EventType.ShowLoadingBar');
            showLoadingBar();
        },
    });

    useEventBusOn({
        eventType: EventType.HideLoadingBar,
        listener: () => {
            console.log('### EventType.HideLoadingBar');
            hideLoadingBar();
        },
    });

    //**********************************************
    //세션타이머 이벤트 처리
    //**********************************************
    useEventBusOn({
        eventType: EventType.StartSessionTimer,
        listener: async (args) => {
            const arr = args as [number];
            const timeout = arr[0];
            console.debug('# start session timer timeout=', timeout);

            if (channel === Channel.MB) {
                doNStartSessionTimer(timeout);
                return;
            }

            if (channel === Channel.IB) {
                sessionTimerManager.start(timeout);
                return;
            }
        },
    });

    useEventBusOn({
        eventType: EventType.ResetSessionTimer,
        listener: async () => {
            console.debug('# reset session timer');

            if (!isLogin()) {
                return;
            }

            if (channel === Channel.MB) {
                doNResetSessionTimer();
                return;
            }

            if (channel === Channel.IB) {
                sessionTimerManager.restart();
                return;
            }
        },
    });

    useEventBusOn({
        eventType: EventType.SecureKeypadClear,
        listener: async (...args: unknown[]) => {
            const fieldId = args?.[0];

            if (fieldId) {
                clear && clear?.(fieldId as string);
                subClear && subClear?.(fieldId as string);
            } else {
                clear && clear?.();
                subClear && subClear?.();
            }
        },
    });

    useEventBusOn({
        eventType: EventType.ClearSessionTimer,
        listener: async () => {
            console.debug('# clear session timer');

            if (channel === Channel.IB) {
                sessionTimerManager.clear();
            } else {
                doNLogout('N');
            }
        },
    });

    // 네이티브 세션팝업 로그아웃 이벤트 처리
    useEventBusOn({
        eventType: EventType.LogoutAction,
        listener: () => {
            doLogout({
                onLogout: async () => {
                    if (channel === Channel.MB) {
                        const res = await doNAutoLogout({ type: '1' });
                        if (!res) {
                            return;
                        }

                        const { type } = res;
                        console.log('### EventType.LogoutAction type', type);
                    }
                    goLogin({ isGoPreLogin: true });
                },
            });
        },
    });

    // 네이티브 세션팝업 세션만료 이벤트
    useEventBusOn({
        eventType: EventType.ExpireSession,
        listener: () => {
            if (channel === Channel.MB) {
                doLogout({
                    onLogout: async () => {
                        const res = await doNAutoLogout({ type: '1' });
                        if (!res) {
                            return;
                        }

                        // Error Store 클리어
                        errorClear();

                        // Popup Store 클리어
                        resetPopup();

                        const { type } = res;
                        console.log('### EventType.ExpireSession type', type);
                        goLogin({ isGoPreLogin: true });
                    },
                });
            }
        },
    });

    // 네이티브 세션팝업 세션연장 이벤트
    useEventBusOn({
        eventType: EventType.ExtendSession,
        listener: () => {
            extendSession();
        },
    });

    //**********************************************
    //단말기 상태바 색깔 변경 이벤트
    //**********************************************
    useEventBusOn({
        eventType: EventType.ChangeStatusBarColor,
        listener: async (...args: unknown[]) => {
            if (channel !== Channel.MB) {
                return;
            }

            const res = await doNGetCurrentWebviewStatus();
            const { isTop } = res;

            const arr = args as [string];
            const color = arr[0] as '0' | '1';

            console.log('# doNSetStatusBarIconColor color', color);

            //현재 상태바 color 를 store에 저장한다.
            setStatusBarColor(color);

            console.log('# CSD navbar isTop', isTop, location.href);

            if (!isTop) {
                return;
            }

            doNSetStatusBarIconColor(color);
        },
    });

    //*******************************************
    // 네이티브 단말기 상태바 color 동기화 처리
    //*******************************************
    const syncStatusBarColor = async () => {
        const statusBarColor = getStatusBarColor();
        if (statusBarColor) {
            doNSetStatusBarIconColor(statusBarColor);
        }
    };

    useWebviewReactivated({
        onReactivated: async () => {
            syncStatusBarColor();
        },

        onResume: async () => {
            syncStatusBarColor();
        },

        onNavbarClick: async () => {
            syncStatusBarColor();
        },
    });

    return null;
};

export { AppEventSubscriber };
